import { Link } from "react-router-dom";
import { Prose, Unavailable, QuestionNav } from "./Bits";
import { EvidenceChip } from "../EvidenceChip";
import { fmtDate } from "../useSciPreview";

export function SubjectHero({ d }) {
  const lede = d.editorial.hero_lede[0];
  return (
    <header className="py-14 lg:py-24" data-testid="sci-subject-hero">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#92400E]">Subject guide · {d.subject.family}</p>
      <h1 className="mt-4 font-editorial text-5xl text-stone-900 sm:text-6xl lg:text-7xl" data-testid="sci-subject-title">{d.subject.name}</h1>
      {lede && <Prose item={lede} testId="sci-subject-lede" className="mt-6 max-w-3xl font-editorial text-xl leading-relaxed text-stone-700 md:text-2xl" />}
      <dl className="mt-10 grid max-w-3xl grid-cols-2 gap-6 md:grid-cols-4" data-testid="sci-subject-stats">
        <Stat k="UK universities" v={d.offer.uk.universities} note="with a Philosophy course" testId="sci-stat-uk" />
        <Stat k="Single honours" v={d.offer.uk.single_honours_universities} note="UK universities" testId="sci-stat-single" />
        <Stat k="US institutions" v={d.offer.us.universities} note="Scorecard CIP 38.01" testId="sci-stat-us" />
        <Stat k="WUA subject ranking" v="In development" note="validated with this guide" small testId="sci-stat-ranking" />
      </dl>
      <p className="mt-3 text-[11px] text-stone-500">Counts from WUA's catalogue on {fmtDate(d.offer.generated_at)}. See “How widely is it offered?” for the method.</p>
      <div className="mt-10"><QuestionNav /></div>
    </header>
  );
}

const Stat = ({ k, v, note, small, testId }) => (
  <div data-testid={testId}>
    <dt className="font-mono text-[10px] uppercase tracking-wider text-stone-500">{k}</dt>
    <dd className={`mt-1 ${small ? "font-editorial text-lg leading-tight" : "font-mono text-3xl"} text-stone-900`}>{v}</dd>
    {note && <dd className="text-[11px] text-stone-500">{note}</dd>}
  </div>
);

export function WhatIs({ d }) {
  const e = d.editorial;
  const modules = d.modules.filter((m) => !m.joint).flatMap((m) => m.optional.slice(0, 3).map((c) => ({ ...c, university: m.university }))).slice(0, 8);
  return (
    <div className="space-y-14">
      <div className="grid gap-x-12 gap-y-6 md:grid-cols-2" data-testid="sci-what-intro">
        {e.what_intro.map((it, i) => <Prose key={i} item={it} testId={`sci-what-${i}`} className={`leading-relaxed text-stone-800 ${i === 0 ? "font-editorial text-xl md:text-2xl" : "text-base md:text-lg"}`} />)}
      </div>
      <div>
        <h3 className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Questions you'll wrestle with</h3>
        {e.questions.length ? (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="sci-example-questions">
            {e.questions.map((it, i) => (
              <li key={i} className="rounded-xl bg-[#002147] px-5 py-5 text-white">
                <Prose item={it} testId={`sci-example-q-${i}`} className="font-editorial text-xl leading-snug [&_button]:text-white/60 [&_button:hover]:text-emerald-200 [&_button:hover]:bg-white/10" />
              </li>))}
          </ul>
        ) : <Unavailable testId="sci-example-q-none">Example questions aren't verified yet.</Unavailable>}
      </div>
      <div>
        <h3 className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Real options on these courses</h3>
        <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="sci-curious-modules">
          {modules.map((c, i) => (
            <li key={i} data-testid={`sci-curious-module-${i}`} className="flex items-start justify-between gap-2 rounded-xl border border-stone-200 bg-white p-3 sm:p-4">
              <span><span className="block text-sm text-stone-900 sm:text-[15px]">{c.value}</span><span className="text-xs text-stone-500">{c.university}</span></span>
              <EvidenceChip claim={c} subtle testId={`sci-curious-module-${i}-source`} />
            </li>))}
        </ul>
      </div>
    </div>
  );
}

export const ProfileCta = () => <Link to="/profile" data-testid="sci-profile-link" className="text-sm font-medium text-[#002147] underline underline-offset-2">Update your Profile</Link>;
